import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// HTML escaping function to prevent XSS attacks
export function escapeHtml(text: string): string {
  if (!text) return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Sanitize URLs to prevent javascript: protocols
export function sanitizeUrl(url: string): string {
  if (!url) return ''
  const trimmedUrl = url.trim()
  if (trimmedUrl.toLowerCase().startsWith('javascript:') || 
      trimmedUrl.toLowerCase().startsWith('data:') ||
      trimmedUrl.toLowerCase().startsWith('vbscript:')) {
    return '#'
  }
  return trimmedUrl
}

// Comprehensive input sanitization system
export function sanitizeTextInput(input: string, options: {
  maxLength?: number
  allowBasicFormatting?: boolean
  preventHTML?: boolean
} = {}): string {
  if (!input) return ''
  
  const { maxLength = 1000, allowBasicFormatting = false, preventHTML = true } = options
  
  let sanitized = input.trim()
  
  if (preventHTML) {
    // Remove potentially dangerous HTML tags and attributes
    sanitized = sanitized
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/<object[^>]*>.*?<\/object>/gi, '')
      .replace(/<embed[^>]*>/gi, '')
      .replace(/<link[^>]*>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      .replace(/<style[^>]*>.*?<\/style>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')
      .replace(/data:/gi, '')
      .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick, onload, etc.
    
    if (!allowBasicFormatting) {
      // Remove all HTML tags
      sanitized = sanitized.replace(/<[^>]*>/g, '')
    }
  }
  
  return sanitized.slice(0, maxLength)
}

// Safe display function for user content (for rendering in UI)
export function safeDisplayText(text: string): string {
  return escapeHtml(sanitizeTextInput(text, { preventHTML: true }))
}

// Safe function specifically for print output - strips HTML and escapes for HTML context
export function safePrintText(text: string): string {
  if (!text) return ''
  
  // Create a temporary element to decode any existing HTML entities first
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = text
  let decoded = tempDiv.textContent || tempDiv.innerText || ''
  
  // Strip any remaining HTML tags completely (in case of nested or malformed HTML)
  decoded = decoded.replace(/<[^>]*>/g, '')
  
  // Now escape special characters for HTML context to prevent any HTML rendering
  return decoded
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

// Safe storage function (for saving to database)
export function safeSaveText(text: string, maxLength: number = 1000): string {
  return sanitizeTextInput(text, { maxLength, preventHTML: true })
}

// Input validation hook for forms
export function validateFormInput(input: string, rules: {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  preventHTML?: boolean
}): { isValid: boolean; error?: string; sanitized: string } {
  const { required = false, minLength = 0, maxLength = 1000, pattern, preventHTML = true } = rules
  
  if (!input && required) {
    return { isValid: false, error: 'This field is required', sanitized: '' }
  }
  
  if (!input) {
    return { isValid: true, sanitized: '' }
  }
  
  const sanitized = sanitizeTextInput(input, { maxLength, preventHTML })
  
  if (sanitized.length < minLength) {
    return { isValid: false, error: `Minimum length is ${minLength} characters`, sanitized }
  }
  
  if (pattern && !pattern.test(sanitized)) {
    return { isValid: false, error: 'Invalid format', sanitized }
  }
  
  return { isValid: true, sanitized }
}

// Legacy function kept for backward compatibility - DO NOT USE IN NEW CODE
export function validateUserInput(input: string, maxLength: number = 500): string {
  // Removed console.warn for production - function is deprecated
  return sanitizeTextInput(input, { maxLength, preventHTML: true })
}
